import { StatusChip } from "components/StatusChip";
import { Summon } from "types";
import { CheckCircle, Clock, User } from "lucide-react";
import { useTranslation } from "contexts/i18nContext";

interface SummonProps {
  summonData: Summon;
}

export default function SummonConfirmationStatus({ summonData }: SummonProps) {
  const { t } = useTranslation();

  const getStatusIcon = (confirmed: boolean) => {
    return confirmed ? (
      <CheckCircle className="w-4 h-4 text-dark-red-600" />
    ) : (
      <Clock className="w-4 h-4 text-gray-400" />
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-6">
        <div className="p-2 rounded-lg bg-dark-red-50 dark:bg-dark-red-900/20">
          <User className="w-5 h-5 text-dark-red-600 dark:text-dark-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {t("summonConfirmationStatus")}
        </h3>
      </div>

      <div className="space-y-5">
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-dark-red-500"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("coordinator")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(!!summonData?.coordinator_confirmed)}
            <StatusChip
              status={
                summonData?.coordinator_confirmed
                  ? t("confirmed")
                  : t("pending")
              }
            />
          </div>
        </div>

        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-dark-red-500"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("accusedAdvocate")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(!!summonData?.accused_advocate_confirmed)}
            <StatusChip
              status={
                summonData?.accused_advocate_confirmed
                  ? t("confirmed")
                  : t("pending")
              }
            />
          </div>
        </div>

        {/* Uncomment if needed in future */}
        {/* <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-dark-red-500"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("accused")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {getStatusIcon(summonData?.accused_confirmed)}
            <StatusChip
              status={summonData?.accused_confirmed ? t("confirmed") : t("pending")}
              variant={summonData?.accused_confirmed ? "success" : "warning"}
            />
          </div>
        </div> */}
      </div>

      {/* Summary Footer */}
      <div className="mt-6 pt-5 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {t("overallStatus")}
          </span>
          <span
            className={`text-sm font-medium ${
              summonData?.coordinator_confirmed &&
              summonData?.accused_advocate_confirmed
                ? "text-dark-red-600 dark:text-dark-red-400"
                : "text-amber-600 dark:text-amber-400"
            }`}
          >
            {summonData?.coordinator_confirmed &&
            summonData?.accused_advocate_confirmed
              ? t("fullyConfirmed")
              : t("partiallyConfirmed")}
          </span>
        </div>
      </div>
    </div>
  );
}
